import React from 'react';
import {twMerge} from 'tailwind-merge';
import {cva} from 'class-variance-authority';
import {buttonSizes} from 'utils/uiSizes';
import {getPalette} from 'components/ui/colorScheme/core/palettes';
import {resolveColor} from 'components/ui/colorScheme/core/resolveColor';
import {resolveTone} from 'components/ui/colorScheme/core/resolveTone';

const disabledFilled =
	'disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed disabled:text-gray-400 dark:disabled:text-gray-500 disabled:opacity-70 disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-800';

const disabledOutline =
	'disabled:bg-transparent disabled:text-gray-400 dark:disabled:text-gray-600 disabled:border-gray-300 dark:disabled:border-gray-700 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent';

const disabledGhost =
	'disabled:bg-transparent disabled:text-black/40 dark:disabled:text-gray-500 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent';

const baseVariants = cva(
	[
		'flex items-center justify-center rounded-md',
		'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-opacity-20',
		'transition-colors active:transition-none',
	].join(' '),
	{
		variants: {
			size: {
				xs: buttonSizes.xs,
				sm: buttonSizes.sm,
				default: buttonSizes.default,
				md: buttonSizes.md,
				lg: buttonSizes.lg,
			},
			effect: {
				lifted: 'shadow-lift active:shadow-lift-sm',
				default: 'shadow-none',
			},
		},
		defaultVariants: {
			size: 'default',
			effect: 'default',
		},
	},
);

const VARIANT_MAP = {
	default: {style: 'filled', color: 'neutral', tone: 'soft'},
	white: {style: 'filled', color: 'white', tone: 'soft'},
	blue: {style: 'filled', color: 'blue', tone: 'strong'},
	indigo: {style: 'filled', color: 'indigo', tone: 'strong'},
	green: {style: 'filled', color: 'green', tone: 'strong'},
	red: {style: 'filled', color: 'red', tone: 'strong'},
	yellow: {style: 'filled', color: 'yellow', tone: 'strong'},
	purple: {style: 'filled', color: 'purple', tone: 'strong'},
	dark: {style: 'filled', color: 'neutral', tone: 'strong'},
	outline: {style: 'outline', color: 'neutral', tone: 'soft'},
	ghost: {style: 'ghost', color: 'neutral', tone: 'soft'},
};

function resolveButtonProps(variant, appearance, colorProp, toneProp) {
	// New API: appearance explicitly sets structural intent
	if (appearance) {
		return {
			style: appearance,
			color: resolveColor(colorProp || 'neutral'),
			tone: resolveTone(toneProp),
		};
	}

	// Explicit color without appearance: derive style from variant or default to filled
	if (colorProp) {
		const isStructural = variant === 'outline' || variant === 'ghost';
		return {
			style: isStructural ? variant : 'filled',
			color: resolveColor(colorProp),
			tone: resolveTone(toneProp),
		};
	}

	// Legacy variant mapping
	const mapped = VARIANT_MAP[variant] || VARIANT_MAP.default;
	return {
		...mapped,
		tone: toneProp ? resolveTone(toneProp) : mapped.tone,
	};
}

function joinSlots(...slots) {
	return slots.flat(Infinity).filter(Boolean).join(' ');
}

function getStyleClasses(style, palette) {
	switch (style) {
		case 'outline':
			return joinSlots(
				'bg-transparent border',
				palette.border,
				palette.text,
				palette.hoverBg,
				palette.activeBg,
				disabledOutline,
			);
		case 'ghost':
			return joinSlots(
				'bg-transparent',
				palette.text,
				palette.hoverBg,
				palette.activeBg,
				disabledGhost,
			);
		default:
			return joinSlots(
				palette.bg,
				palette.text,
				palette.hoverBg,
				palette.activeBg,
				disabledFilled,
			);
	}
}

export const buttonVariants = (props = {}) => {
	const {variant, appearance, color, tone, size, effect, className} = props;
	const resolved = resolveButtonProps(variant, appearance, color, tone);
	const palette = getPalette(resolved.color, resolved.tone);

	return twMerge(
		baseVariants({size, effect}),
		getStyleClasses(resolved.style, palette),
		className,
	);
};

export const Button = React.forwardRef(
	(
		{
			children,
			className,
			variant,
			appearance,
			color,
			tone,
			size,
			effect,
			type = 'button',
			...props
		},
		ref,
	) => (
		<button
			type={type}
			className={buttonVariants({
				variant,
				appearance,
				color,
				tone,
				size,
				effect,
				className,
			})}
			ref={ref}
			{...props}
		>
			{children}
		</button>
	),
);

Button.displayName = 'Button';

export default Button;
